import React, { useState } from "react";
import { useAtom } from "jotai";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    ThemeProvider
} from "@mui/material";
import { mbxUserAtom, allCategoriesAtom } from "../atoms/atoms";
import LoadingButton from "@mui/lab/LoadingButton";
import { postApi } from "../modules/AmplifyServices";
import { useTheme } from "@emotion/react";
import { DialogTheme } from "../styles/DialogTheme";
import { auditCategoryCreate } from "../modules/Audit";

const AddCategoriesOnTheFlyDialog = ({ open, onClose, onCategoryCreated }) => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const [categories, setCategories] = useAtom(allCategoriesAtom);
    const [categoryName, setCategoryName] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");

    const theme = useTheme();

    const validateCategoryName = (name) => {
        const trimmedName = name.trim().toUpperCase();
        return categories.find(
            (c) => c.name.trim().toUpperCase() === trimmedName
        );
    };

    const handleAddCategory = async () => {
        setError("");
        if (categoryName.trim() === "") {
            setError("Category name is required.");
            return;
        }
        const duplicate = validateCategoryName(categoryName);
        if (duplicate) {
            setError("Category name already exists.");
            return;
        }
        setIsSaving(true);
        try {
            const options = {
                body: {
                    companyId: mbxUser.companyId,
                    name: categoryName,
                    desc: categoryName
                }
            };

            const result = await postApi(
                "serviceManagement",
                "/category",
                options
            );
            if (result && result.category) {
                await auditCategoryCreate(mbxUser, result.category);
                setCategories([...categories, result.category]);
                setCategoryName("");
                if (onCategoryCreated) {
                    onCategoryCreated(result.category); //Pass new category to parent
                }
            }
        } catch (error) {
            setError("Failed to create category.");
            console.error(`Error creating category ${categoryName}:`, error);
        }
        onClose();
        setIsSaving(false);
    };

    const handleClose = () => {
        setCategoryName("");
        setError("");
        onClose();
    };

    const handleChange = (e) => {
        setCategoryName(e.target.value);
        setError("");
    };

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ paddingBottom: "18px" }}>
                    Add service category
                </DialogTitle>
                <DialogContent>
                    <Grid item xs={12} sm={12} sx={{ paddingTop: "18px" }}>
                        <TextField
                            autoFocus
                            label="Category name"
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={categoryName}
                            onChange={handleChange}
                            error={!!error}
                            helperText={error}
                        />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid
                        container
                        item
                        gap={"18px"}
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end"
                        }}
                    >
                        <Button
                            variant="cancelButton"
                            onClick={handleClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            color="primary"
                            variant="contained"
                            onClick={handleAddCategory}
                            loading={isSaving}
                            disabled={!categoryName}
                            sx={{ marginLeft: "0px !important" }}
                        >
                            Save
                        </LoadingButton>
                    </Grid>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default AddCategoriesOnTheFlyDialog;
